import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, CheckSquare } from "lucide-react";
import { LOST_REASONS } from "@/lib/crm";

const OUTCOMES = ["Nessuna risposta", "Richiamare", "Interessato", "Sta valutando", "Richiede modifica preventivo", "Non interessato", "Accettato", "Perso"];

export default function CompleteActivityDialog({ open, onOpenChange, activity, onSaved }) {
  const [outcome, setOutcome] = useState("Interessato");
  const [nextDate, setNextDate] = useState("");
  const [lostReason, setLostReason] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (open) { setError(""); setOutcome("Interessato"); setNextDate(""); setLostReason(""); setNotes(""); } }, [open]);

  const needsDate = outcome === "Richiamare";
  const needsReason = ["Non interessato", "Perso"].includes(outcome);

  const submit = async () => {
    if (needsDate && !nextDate) { setError("Per 'Richiamare' indica la nuova data di contatto"); return; }
    if (needsReason && !lostReason) { setError("Indica il motivo di chiusura"); return; }
    setError(""); setSaving(true);
    try {
      const { data } = await api.post(`/activities/${activity.id}/complete`, {
        outcome, next_contact_date: nextDate || null, lost_reason: needsReason ? lostReason : null, notes: notes || null,
      });
      toast.success("Contatto registrato");
      onSaved?.(data); onOpenChange(false);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-md" data-testid="complete-activity-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><CheckSquare className="h-5 w-5 text-brand" /> Registra esito contatto</DialogTitle>
          <DialogDescription>{activity?.type} · {activity?.customer_name}</DialogDescription>
        </DialogHeader>
        {error && <div className="rounded-lg border border-danger/25 bg-danger/10 px-3 py-2 text-sm text-danger" data-testid="complete-error">{error}</div>}
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Esito *</Label>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger data-testid="complete-outcome"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{OUTCOMES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {needsDate && <div className="space-y-1.5"><Label>Nuova data di contatto *</Label><Input type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} data-testid="complete-next-date" /></div>}
          {needsReason && <div className="space-y-1.5"><Label>Motivo *</Label>
            <Select value={lostReason} onValueChange={setLostReason}>
              <SelectTrigger data-testid="complete-reason"><SelectValue placeholder="Seleziona motivo" /></SelectTrigger>
              <SelectContent className="bg-white">{LOST_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select></div>}
          <div className="space-y-1.5"><Label>Note</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="complete-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="complete-cancel">Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="complete-confirm">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Conferma"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
